import { components } from "./apex-client-gen";
import { getMajorPurchaseGlidingFrameworks } from "@/common/widgets/portfolio/gliding-framework-details";
import { setAssetAllocation } from "@/api/custom";
import { ApexAccount } from "@/types/wealth";
import { isCashSweepSymbol, isClosedOrClosingAccount } from "@wavvest/shared-utils";

const sum = (arr: number[]) => arr.reduce((a, b) => a + b, 0);

export const GOALS = [
  "Just Invest",
  "Retirement",
  "Major Purchase",
  "Supplement Income",
  "High Yield Cash",
] as const;

export const ALLOCATIONS = [
  "Growth",
  "Capital Preservation",
  "Yield",
  "Money Market",
] as const;

export const RETIREMENT_TYPES = [
  "ROTH",
  "TRADITIONAL",
  "ROLLOVER",
  "TAXABLE",
] as const;

export const ASSET_LIABILITY = {
  ASSET: "asset",
  LIABILITY: "liability",
} as const;

export type GoalType = (typeof GOALS)[number];

export type AllocationType = (typeof ALLOCATIONS)[number];

export type RetirementType = (typeof RETIREMENT_TYPES)[number];

export type AssetLiability =
  (typeof ASSET_LIABILITY)[keyof typeof ASSET_LIABILITY];

export const COLORSCHEME: Record<
  string | "Overall",
  Record<AllocationType, string>
> = {
  "Just Invest": {
    Growth: "#4F68E4",
    "Capital Preservation": "#3A4384",
    Yield: "#808DE1",
    "Money Market": "#9CA3AF",
  },
  Retirement: {
    Growth: "#D4C087",
    "Capital Preservation": "#C79F35",
    Yield: "#E5B01F",
    "Money Market": "#9CA3AF",
  },
  "Major Purchase": {
    Growth: "#E49B7A",
    "Capital Preservation": "#DF5C22",
    Yield: "#E9875B",
    "Money Market": "#9CA3AF",
  },
  "Supplement Income": {
    Growth: "#FFFF8B",
    "Capital Preservation": "#F6F625",
    Yield: "#FFFFDD",
    "Money Market": "#9CA3AF",
  },
  Overall: {
    Growth: "#856095",
    "Capital Preservation": "#6F3479",
    Yield: "#BDA1C9",
    "Money Market": "#9CA3AF",
  },
};

export type UserInfo = {
  retirementAge: number;
  longevity: number;
  married: boolean;
  annualIncome: number;
  monthlyExpenses: number;
  totalSavings: number;
  liquidNetWorth: number;
  annualWithdrawal: number;
  house: boolean;
  houseValue?: number;
  mortgageRemaining?: number;
};

export type Portfolio = {
  totalInvested: number;
  allocations: Partial<Record<AllocationType, number>>;
  timeHorizon: number;
  retirementTypes?: RetirementType[];
  glidingModel?: GlidingModel;
};

export type GlidingModel = {
  stoppingPoints: GlidingStoppingPoint[];
};

export type GlidingStoppingPoint = {
  timeUnit: "MONTH";
  timeValue: number;
  allocations: Partial<Record<AllocationType, number>>;
};

export type IncomeExpenseProjection = {
  year: number;
  income: number;
  expenses: number;
  netSavings: number;
  totalSavings: number;
};

export type ProjectedSaving = {
  year: number;
  percentile_5: number;
  percentile_25: number;
  percentile_50: number;
  percentile_75: number;
  percentile_95: number;
};

export type HomePriceProjection = {
  year: number;
  projectionValue: number;
};

export type FinancialPlansState = PortfolioState[];

export type PortfolioState = {
  title?: string;
  userInfo?: UserInfo;
  portfolios: Partial<Record<GoalType, Portfolio>>;
  crisisDiagram?: number[];
  monteCarloSimulation?: [number[], number[], number[]];
  incomeExpenseProjection?: IncomeExpenseProjection[];
  projectionSavings?: ProjectedSaving[];
  homePriceProjection?: HomePriceProjection[];
  probabilityOfSuccess?: number;
  scenarioTimestamp?: string;
  scenarioId?: string;
};

export function defaultPortfolio(): Portfolio {
  return {
    totalInvested: 0,
    timeHorizon: 0,
    allocations: {
      Growth: 0,
      "Capital Preservation": 0,
      "Money Market": 0,
    },
  };
}

export function entries<K extends string | number | symbol, V>(
  object: Record<K, V> | Partial<Record<K, V>>,
): [K, V][] {
  return Object.entries(object) as [K, V][];
}

// result of user's answer: key is question id (string), value is answer id (like "a","b"... )
export type QuestionAssessmentResults = Record<string, string>;

// type of each answer option in RA_DataSet.json
export interface AnswerOption {
  id: string;
  answer: string;
  "Points Towards Growth": number;
  "Points Towards capital preservation": number;
  "Points Towards Yield": number;
}

// type of each question in RA_DataSet.json
export interface Question {
  context: string;
  question: string;
  answers: AnswerOption[];
}

// final asset allocation structure
export interface BundleDistribution {
  growth: number; // growth investment ratio
  preservation: number; // conservative investment ratio
  yield: number; // yield investment ratio
}

/**
 * calculate final asset allocation ratio based on user's answer and JSON data set
 *
 * @param questionAssessmentResults - user's answer
 * @param jsonData - parsed array from RA_DataSet.json
 * @returns BundleDistribution
 */
export function calculateFinalDistribution(
  questionAssessmentResults: QuestionAssessmentResults,
  jsonData: Question[],
): BundleDistribution {
  const distribution: BundleDistribution = {
    growth: 0,
    preservation: 0,
    yield: 0,
  };

  const entries = Object.entries(questionAssessmentResults);

  for (let index = 0; index < entries.length; index++) {
    if (index >= jsonData.length) break;

    const [, answerId] = entries[index];
    const answers = jsonData[index].answers || [];

    // find matched answer option
    const matched = answers.find(
      (opt) => opt.id.toLowerCase() === answerId.toLowerCase(),
    );
    if (matched) {
      distribution.growth += matched["Points Towards Growth"] ?? 0;
      distribution.preservation +=
        matched["Points Towards capital preservation"] ?? 0;
      // note: original Python logic did not directly add "Points Towards Yield"
    }
  }

  // calculate yield investment ratio (fixed base 66.6666667)
  const yieldValue =
    66.6666667 - (distribution.growth + distribution.preservation);
  distribution.yield = Math.max(yieldValue, 0);

  return distribution;
}

export function adjustAllocations(
  growth: number,
  capitalPreservation: number,
): { growth: number; capitalPreservation: number } {
  const total = growth + capitalPreservation;
  if (total === 0) return { growth: 0, capitalPreservation: 0 };
  const factor = 100 / total;
  const g = growth * factor;
  const c = capitalPreservation * factor;
  // separate integer and decimal
  const arr = [
    { name: "growth" as const, value: g },
    { name: "capitalPreservation" as const, value: c },
  ].map((x) => ({
    ...x,
    int: Math.floor(x.value),
    frac: x.value - Math.floor(x.value),
  }));
  const sumInt = arr.reduce((s, x) => s + x.int, 0);
  const residual = 100 - sumInt;
  // fill in descending order based on frac
  arr.sort((a, b) => b.frac - a.frac);
  for (let i = 0; i < residual; i++) {
    arr[i % arr.length].int++;
  }
  return {
    growth: arr.find((x) => x.name === "growth")!.int,
    capitalPreservation: arr.find((x) => x.name === "capitalPreservation")!.int,
  };
}

export function implementRecommendation(
  goal: GoalType,
  recommendation: Partial<Record<AllocationType, number>>,
  timeHorizon: number,
  existing?: Portfolio,
  retirementTypes?: RetirementType[],
): Portfolio {
  // get or generate initial portfolio
  const p: Portfolio = existing
    ? {
        totalInvested: 0,
        timeHorizon,
        allocations: { ...existing.allocations },
        retirementTypes: existing.retirementTypes,
      }
    : {
        totalInvested: 0,
        timeHorizon,
        allocations: {},
        retirementTypes: retirementTypes,
      };

  switch (goal) {
    case "Supplement Income":
      ALLOCATIONS.forEach((a) => (p.allocations[a] = undefined));
      p.allocations["Yield"] = 100;
      break;
    case "High Yield Cash":
      ALLOCATIONS.forEach((a) => (p.allocations[a] = undefined));
      p.allocations["Money Market"] = 100;
      break;
    case "Major Purchase": {
      const framework = getMajorPurchaseGlidingFrameworks(timeHorizon);
      p.allocations = { ...framework.startingAllocation };
      // if need to keep glidingModel, please append it
      break;
    }
    default: {
      // normal case
      ALLOCATIONS.forEach((a) => {
        p.allocations[a] = recommendation[a] ?? 0;
      });
      // clear non Growth/CapPres
      p.allocations["Yield"] = undefined;
      p.allocations["Money Market"] = undefined;
      // time range adjustment
      if (timeHorizon >= 10) {
        p.allocations["Growth"]! += 33;
      } else {
        p.allocations["Growth"]! += 11;
        p.allocations["Capital Preservation"]! += 22;
      }
      // normalize Growth & CapPres
      const { growth, capitalPreservation } = adjustAllocations(
        p.allocations["Growth"]!,
        p.allocations["Capital Preservation"]!,
      );
      p.allocations["Growth"] = growth;
      p.allocations["Capital Preservation"] = capitalPreservation;
    }
  }
  return p;
}

export type SetAssetAllocationRequest =
  components["schemas"]["AssetAllocationIn"];

export function allocationFromFinancialPlan(
  portfolio: Portfolio,
): SetAssetAllocationRequest {
  let request: SetAssetAllocationRequest;
  if (
    (portfolio.allocations.Growth ?? 0) > 0 ||
    (portfolio.allocations["Capital Preservation"] ?? 0) > 0
  ) {
    request = {
      kind: "Risk Based Framework",
      risk_score: portfolio.allocations.Growth ?? 0,
      time_horizon_years: portfolio.timeHorizon,
    };
  } else if ((portfolio.allocations.Yield ?? 0) > 0) {
    request = {
      kind: "Yield",
      risk_score: 0,
      time_horizon_years: portfolio.timeHorizon,
    };
  } else {
    request = {
      kind: "Short term T-Bills",
      risk_score: 0,
      time_horizon_years: portfolio.timeHorizon,
    };
  }
  return request;
}

export async function setAllocationForAllAccounts(
  accounts: ApexAccount[],
  portfolios: Record<GoalType, Portfolio>,
) {
  const tasks: Promise<void>[] = [];
  accounts.map((account) => {
    const goal = accountTypeToGoalType(account.type);
    const portfolio = portfolios[goal];
    if (portfolio) {
      const request = allocationFromFinancialPlan(portfolio);
      tasks.push(setAssetAllocation(account.id, request));
    }
  });
  await Promise.all(tasks);
}

export const ACCOUNT_TYPES = [
  "RETIREMENT_TRADITIONAL",
  "RETIREMENT_ROTH",
  "RETIREMENT_ROLLOVER",
  "RETIREMENT_TAXABLE",
  "JUST_INVEST",
  "MAJOR_PURCHASE",
  "SUPPLEMENTAL_INCOME",
  "HIGH_YIELD_CASH",
] as const;

export type AccountType = (typeof ACCOUNT_TYPES)[number];

export function accountTypeToGoalType(type: AccountType): GoalType {
  switch (type) {
    case "RETIREMENT_TRADITIONAL":
    case "RETIREMENT_ROTH":
    case "RETIREMENT_TAXABLE":
    case "RETIREMENT_ROLLOVER":
      return "Retirement";
    case "JUST_INVEST":
      return "Just Invest";
    case "MAJOR_PURCHASE":
      return "Major Purchase";
    case "SUPPLEMENTAL_INCOME":
      return "Supplement Income";
    case "HIGH_YIELD_CASH":
      return "High Yield Cash";
  }
}

export function accountTypeToName(type: AccountType): string {
  switch (type) {
    case "RETIREMENT_TRADITIONAL":
      return "Traditional IRA";
    case "RETIREMENT_ROTH":
      return "Roth IRA";
    case "RETIREMENT_TAXABLE":
      return "Taxable Retirement";
    case "RETIREMENT_ROLLOVER":
      return "Rollover IRA";
    case "JUST_INVEST":
      return "Just Invest";
    case "MAJOR_PURCHASE":
      return "Major Purchase";
    case "SUPPLEMENTAL_INCOME":
      return "Supplement Income";
    case "HIGH_YIELD_CASH":
      return "High Yield Cash";
  }
}
/**
 * Maps a goal type and retirement type to the corresponding account type.
 *
 * @param {GoalType} goal - The goal type
 * @param {RetirementType | null} retirementType - The retirement type (for retirement goals)
 * @returns {AccountType | null} The corresponding account type, or null if no mapping exists
 */
export function goalTypeToAccountType(
  goal: GoalType,
  retirementType: RetirementType | null,
): AccountType | null {
  if (goal === "Retirement") {
    switch (retirementType) {
      case "TRADITIONAL":
        return "RETIREMENT_TRADITIONAL";
      case "ROLLOVER":
        return "RETIREMENT_ROLLOVER";
      case "ROTH":
        return "RETIREMENT_ROTH";
      case "TAXABLE":
        return "RETIREMENT_TAXABLE";
      default:
        return null;
    }
  } else {
    switch (goal) {
      case "Just Invest":
        return "JUST_INVEST";
      case "Major Purchase":
        return "MAJOR_PURCHASE";
      case "Supplement Income":
        return "SUPPLEMENTAL_INCOME";
      case "High Yield Cash":
        return "HIGH_YIELD_CASH";
      default:
        return null;
    }
  }
}

export type AddAccountRequest = components["schemas"]["AddAccounts"];

export type AccountDetail = components["schemas"]["Account"];

export type DepositInfo = {
  account_id: string;
  plaid_id: string;
  deposit_id: string;
  created_at: string;
  amount: string;
  state: string;
  message: string;
  state_category: "SUCCESS" | "FAILED" | "PENDING" | "CANCELED" | "UNKNOWN";
};

export type WithdrawalInfo = {
  account_id: string;
  plaid_id: string;
  withdrawal_id: string;
  created_at: string;
  amount: string;
  state: string;
  message: string;
  state_category: "SUCCESS" | "FAILED" | "PENDING" | "CANCELED" | "UNKNOWN";
};

export type GetIraDistributionConstraintsResponse =
  components["schemas"]["DistributionConstraints"];
export type GetIraContributionConstraintsResponse =
  components["schemas"]["ContributionConstraints"];
export type HoldingPosition = components["schemas"]["Position"];
export type HistoricalPosition =
  components["schemas"]["ClosingPositionSnapshotRecord"];

export type AccountInfo = {
  goal: AccountType;
  account_detail: AccountDetail;
  cash_available_for_withdraw: string;
  open_balance: string;
  total_equity: string;
  buying_power: string;
  advisor_arch_goal?: components["schemas"]["AssetAllocationOut"] | null;
  ira_contribution_constraints?: GetIraContributionConstraintsResponse;
  ira_distribution_constraints?: GetIraDistributionConstraintsResponse;
  deposits?: DepositInfo[];
  withdrawals?: WithdrawalInfo[];
  holdings?: HoldingPosition[];
  portfolios?: Partial<Record<AllocationType, number>>;
  historical_positions?: HistoricalPosition[];
};

export type Account = {
  account_id: string;
  account_number: string;
  account_investment_type: AccountType;
  total_equity: string;
  last_total_date?: string | null;
  account_detail?: AccountDetail;
  historical_positions?: HistoricalPosition[];
};

export type GetAccountsResponse =
  components["schemas"]["CreateOrGetAccountsResponse"];

export type AssetMap = components["schemas"]["GetAssetMap"];

export type AdvisorArchAssetClass =
  components["schemas"]["AdvisorArchAssetClass"];

export type CreateAccountAndNaturalPersonalRequest =
  components["schemas"]["CreateAccountsRequest"];

export type CreateAccountsResponse =
  components["schemas"]["CreateOrGetAccountsResponse"];

export type SignAgreementsRequest =
  components["schemas"]["SignAgreementsRequest"];

export function accountInfoFromApi(
  data: CreateAccountsResponse["accounts"][number],
): AccountInfo {
  let portfolios: Partial<Record<AllocationType, number>> | undefined =
    undefined;
  if (data.advisor_arch_goal) {
    const aa_kind = data.advisor_arch_goal.kind;
    if (
      aa_kind === "GROWTH_X_CAPITAL_PRESERVATION" ||
      aa_kind === "Risk Based Framework"
    ) {
      portfolios = {
        Growth: data.advisor_arch_goal.risk_score,
        "Capital Preservation": 100 - data.advisor_arch_goal.risk_score,
      };
    } else if (aa_kind === "MONEY_MARKET" || aa_kind === "Short term T-Bills") {
      portfolios = {
        "Money Market": 100,
      };
    } else {
      portfolios = {
        Yield: 100,
      };
    }
  }
  const account: AccountInfo = {
    goal: data.goal,
    account_detail: data.account_detail,
    cash_available_for_withdraw: data.cash_available_for_withdraw,
    open_balance: data.open_balance,
    total_equity: data.total_equity,
    buying_power: data.buying_power,
    advisor_arch_goal: data.advisor_arch_goal,
    ira_contribution_constraints:
      data.ira_contribution_constraints ?? undefined,
    ira_distribution_constraints:
      data.ira_distribution_constraints ?? undefined,
    deposits: data.deposits,
    withdrawals: data.withdrawals,
    holdings: data.holdings,
    portfolios: portfolios,
    historical_positions: data.historical_positions,
  };
  return account;
}

export type HoldingPositionNumerical = Omit<HoldingPosition, "amount"> & {
  amount: number;
  unit: "USD" | "Unit";
};

export function makeHoldingsNumerical(
  holding: HoldingPosition,
): HoldingPositionNumerical {
  let amount = parseFloat(holding.amount);
  let unit: "USD" | "Unit" = "USD";
  if (holding.asset_type != "CURRENCY" && holding.stock_unit_price) {
    amount = parseFloat(holding.stock_unit_price) * amount;
  }
  if (holding.asset_type != "CURRENCY" && !holding.stock_unit_price) {
    unit = "Unit";
  }
  return {
    ...holding,
    amount: amount,
    unit: unit,
  };
}

export function mergeHoldings(
  holdings: Array<HoldingPositionNumerical>,
): Array<HoldingPositionNumerical> {
  return holdings
    .sort((a, b) => (a.asset_id < b.asset_id ? 1 : -1))
    .reduce((accu, asset) => {
      if (accu.length === 0) {
        return [asset];
      }
      const [head, ...rest] = accu;
      if (asset.asset_id === head.asset_id) {
        head.amount += asset.amount;
        return [head, ...rest];
      }
      return [asset, head, ...rest];
    }, [] as HoldingPositionNumerical[]);
}

/**
 * get holdings data
 * @param accounts accounts list
 * @param accountId optional account id
 * @param assetMap asset map
 * @param portfolioName optional portfolio name
 * @returns processed holdings data
 */
export function getHoldings(
  accounts: AccountInfo[],
  accountId: string | undefined,
  assetMap: AssetMap | undefined,
  portfolioName: string | undefined,
): HoldingPositionNumerical[] {
  // get relevant assets set
  const relevantAssets = portfolioName
    ? new Set(assetMap?.portfolio_map[portfolioName])
    : undefined;

  // filter asset function
  function filterByAssetMap(h: HoldingPosition) {
    if (portfolioName === undefined) {
      return true;
    }
    if (relevantAssets?.has(h.symbol)) {
      return true;
    }
    return false;
  }

  // get all holdings
  const allHoldings = accounts
    .filter(
      (a) =>
        accountId === undefined || a.account_detail.account_id === accountId,
    )
    .map((a) => a.holdings ?? [])
    .flat()
    .filter(filterByAssetMap)
    .map(makeHoldingsNumerical);

  // if no account id is specified, merge holdings
  if (accountId === undefined) {
    return mergeHoldings(allHoldings);
  }

  return allHoldings;
}

export type Node = {
  id: number;
  text: string;
  valueUsd: number;
  valuePercent: number;
  type: "Security" | "Class";
  children: Array<Node>;
  isLeaf: boolean;
};

export function buildTree(
  holdings: Array<HoldingPositionNumerical>,
  classes: Array<AdvisorArchAssetClass>,
): Node {
  const total = sum(holdings.map((h) => h.amount));
  const moneyMap = new Map();
  for (const h of holdings) {
    moneyMap.set(h.symbol, {
      amount: h.amount,
      percentage: h.amount / total,
    });
    if (isCashSweepSymbol(h.symbol)) {
      // WORKAROUND: AdvisorArch's class tree values cash under the CASH_USD
      // symbol, while Apex reports cash-sweep vehicles (USD, FQLPZ, …) under
      // their own symbols. Remap them so the tree's cash node picks them up.
      moneyMap.set("CASH_USD", {
        amount: h.amount,
        percentage: h.amount / total,
      });
    }
  }

  const processNode = (c: AdvisorArchAssetClass): Node => {
    const children = c.children.map(processNode);
    for (const security of c.securities) {
      children.push({
        id: security.id,
        type: "Security",
        text: security.short_name,
        valueUsd: moneyMap.get(security.symbol)?.amount ?? 0,
        valuePercent: moneyMap.get(security.symbol)?.percentage ?? 0,
        children: [],
        isLeaf: true,
      });
    }
    const valueUsd = sum(children.map((c) => c.valueUsd));
    const valuePercent = sum(children.map((c) => c.valuePercent));
    return {
      id: c.id,
      type: "Class",
      text: c.description,
      valueUsd,
      valuePercent,
      children,
      isLeaf: c.children.length === 0,
    };
  };

  const nodes = classes.map(processNode);
  const root: Node = {
    id: -1,
    text: "__ROOT",
    valueUsd: sum(nodes.map((n) => n.valueUsd)),
    valuePercent: sum(nodes.map((n) => n.valuePercent)),
    type: "Class",
    children: nodes,
    isLeaf: false,
  };
  return root;
}

export type AssetClassesViewProps = {
  accountId?: string;
  portfolioName?: string;
};

export function getAssetClassesView(
  accounts: AccountInfo[],
  accountId: string | undefined,
  assetMap: AssetMap | undefined,
) {
  const holdings = getHoldings(accounts, accountId, assetMap, undefined);
  const classes = assetMap?.class_tree ?? [];
  return buildTree(holdings, classes);
}

export function getHoldingsFromService(
  apexAccounts: ApexAccount[],
  accountId: string | undefined,
  assetMap: AssetMap | undefined,
  portfolioName: string | undefined,
): HoldingPositionNumerical[] {
  // get relevant assets set
  const relevantAssets = portfolioName
    ? new Set(assetMap?.portfolio_map[portfolioName])
    : undefined;

  // filter asset function
  function filterByAssetMap(h: HoldingPosition) {
    if (portfolioName === undefined) {
      return true;
    }
    if (relevantAssets?.has(h.symbol)) {
      return true;
    }
    return false;
  }

  // get all holdings
  const allHoldings = apexAccounts
    .filter((a) => accountId === undefined || a.id === accountId)
    .map((a) => a.holdings ?? [])
    .flat()
    .filter(filterByAssetMap)
    .map(makeHoldingsNumerical);

  // if no account id is specified, merge holdings
  if (accountId === undefined) {
    return mergeHoldings(allHoldings);
  }

  return allHoldings;
}

export const generateAssetClassesView = (
  apexAccounts: ApexAccount[],
  accountId: string | undefined,
  assetMap: AssetMap | undefined,
) => {
  const holdings = getHoldingsFromService(
    apexAccounts,
    accountId,
    assetMap,
    undefined,
  );

  const classes = assetMap?.class_tree ?? [];
  return buildTree(holdings, classes);
};

// Alias an Apex/AdvisorArch class-tree label ("Equity") to the frontend
// allocation category ("Equities") before matching.
const APEX_ASSET_CLASS_ALIASES: Record<string, string> = {
  Equity: "Equities",
};

export const normalizeApexAssetClass = (label: string): string =>
  APEX_ASSET_CLASS_ALIASES[label] ?? label;

// Map an Apex holding's asset_type to a top-level allocation category. Used as a
// fallback for holdings whose symbol is absent from the asset-map class_tree.
// Note: Apex asset_type has no commodity value (a gold ETF such as IAU reports
// EQUITY), so this fallback cannot resolve "Commodities" — those are only
// identifiable via the class_tree, which the primary tree path already handles.
export const classifyApexAssetType = (assetType?: string): string => {
  switch (assetType) {
    case "EQUITY":
    case "MUTUAL_FUND":
    case "DIGITAL":
      return "Equities";
    case "FIXED_INCOME":
      return "Fixed Income";
    case "CURRENCY":
    case "FDIC_SYNTHETIC":
      return "Cash";
    default:
      return "Other";
  }
};

// Allocation for Apex holdings whose symbol is not present in the class_tree.
// buildTree only assigns value to securities defined in the class_tree, so any
// held symbol outside the model portfolio (e.g. ROCQ, ARCI) is otherwise dropped
// entirely. These positions are bucketed by their authoritative asset_type using
// value_usd from the holdings API.
export function getUnclassifiedApexAllocation(
  apexAccounts: ApexAccount[],
  assetMap: AssetMap | undefined,
): Record<string, number> {
  const allocation: Record<string, number> = {
    Equities: 0,
    Commodities: 0,
    "Fixed Income": 0,
    Cash: 0,
    Other: 0,
  };

  const classTreeSymbols = new Set<string>();
  const collect = (nodes: AdvisorArchAssetClass[]) => {
    for (const node of nodes) {
      for (const security of node.securities ?? []) {
        classTreeSymbols.add(security.symbol);
      }
      collect(node.children ?? []);
    }
  };
  collect(assetMap?.class_tree ?? []);

  // buildTree remaps cash-sweep symbols (USD, FQLPZ, …) to CASH_USD. When the
  // class tree defines a CASH_USD security the tree already values those
  // positions, so we skip them here to avoid double-counting. When it does not
  // (e.g. an empty class tree), they would otherwise be dropped, so we bucket
  // them as cash below.
  const treeValuesCash = classTreeSymbols.has("CASH_USD");

  for (const account of apexAccounts) {
    for (const holding of account.holdings ?? []) {
      // Any symbol present in the class tree is already valued there by
      // buildTree (via moneyMap.get(symbol)) — skip to avoid double-counting.
      if (classTreeSymbols.has(holding.symbol)) continue;

      // Cash sweeps are additionally remapped to CASH_USD by buildTree; if the
      // tree defines CASH_USD they're counted there too, otherwise they'd be
      // dropped, so bucket them as cash here.
      const isCashSweep = isCashSweepSymbol(holding.symbol);
      if (isCashSweep && treeValuesCash) continue;

      const raw = (holding as unknown as { value_usd?: string | number })
        .value_usd;
      let value = typeof raw === "number" ? raw : parseFloat(raw ?? "");
      if (!Number.isFinite(value) || value === 0) {
        // Fall back to shares * unit price when value_usd is absent.
        const shares = parseFloat(holding.amount ?? "");
        const price =
          isCashSweep || holding.asset_type === "CURRENCY"
            ? parseFloat(holding.stock_unit_price ?? "") || 1
            : parseFloat(holding.stock_unit_price ?? "");
        value = shares * price;
      }
      if (!Number.isFinite(value) || value === 0) continue;

      allocation[
        isCashSweep ? "Cash" : classifyApexAssetType(holding.asset_type)
      ] += value;
    }
  }

  return allocation;
}

export function formatAccounts(
  apexAccounts: components["schemas"]["CreateOrGetAccountsResponse"],
): AccountInfo[] {
  const incoming = apexAccounts.accounts;
  const accounts: AccountInfo[] = incoming.map(accountInfoFromApi);
  const filteredAccounts = accounts.filter(
    (a) => !isClosedOrPrepareForClosure(a),
  );
  return filteredAccounts.sort((a, b) => (a.goal < b.goal ? -1 : 1));
  // state.mergedHistoricalPositions = buildMergedHistoricalPosition(accounts);
}

function isClosedOrPrepareForClosure(account: AccountInfo) {
  return isClosedOrClosingAccount(account.account_detail);
}
